import React, { useState, useEffect } from 'react';
import studentId from '../config'; // Assuming the studentId is correctly imported
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable'; // For table formatting

// Utility function to format the date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};
 const sessionId = localStorage.getItem("currentSessionId");
 
// Utility function to sort subjects by exam date
const sortByExamDate = (subjects) => {
  return subjects.slice().sort((a, b) => new Date(a.examDate) - new Date(b.examDate));
};

const ExamDatesheet = () => {
  const [examDatesheets, setExamDatesheets] = useState([]);
  const [error, setError] = useState(null);
  const [student, setStudent] = useState(null);

  // Fetch the student details
  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents/${studentId}`,
          { params: { sessionId } }
        );
        setStudent(response.data);
      } catch (err) {
        console.error('Error fetching student:', err);
      }
    };

    fetchStudent();
  }, []);

  useEffect(() => {
    const fetchExamDatesheets = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/exams/getExamByClass/${student.class}`,
          { params: { sessionId } }
        );
        setExamDatesheets(response.data);
      } catch (err) {
        console.error('Error fetching exam datesheets:', err);
        setError('Failed to fetch datesheets. Please try again later.');
      }
    };

    fetchExamDatesheets();
  }, [student]);

  // Function to generate a PDF for each exam datesheet
  const generatePDF = (examDatesheet) => {
    const doc = new jsPDF();

    // Set the document properties and styles
    doc.setFont('helvetica');
    doc.setFontSize(16);
    doc.text(`Exam Datesheet for Class ${student.class}`, 14, 20);
    doc.setFontSize(12);
    doc.text(`Exam: ${examDatesheet.examName}`, 14, 30);

    // Prepare table content
    const subjects = Array.isArray(examDatesheet.subjects) ? examDatesheet.subjects : [];
    const sortedSubjects = sortByExamDate(subjects);
    
    const tableData = sortedSubjects.map((subject, idx) => [
      idx + 1,
      subject.subjectName,
      formatDate(subject.examDate),
      `${subject.startTime} - ${subject.endTime}`,
      subject.room,
      subject.notes || '--',
    ]);

    // Add table with professional look
    doc.autoTable({
      head: [['#', 'Subject', 'Date', 'Timing', 'Room', 'Notes']],
      body: tableData,
      startY: 40,
      theme: 'striped',
      headStyles: { fillColor: [22, 160, 133] },
      margin: { top: 10, left: 14, right: 14 },
    });

    // Add footer with page number
    doc.setFontSize(10);
    doc.text('Generated by School Management System', 14, doc.internal.pageSize.height - 10);

    doc.save(`datesheet_${examDatesheet.examName}_class_${student.class}.pdf`);
  };

  if (examDatesheets.length === 0) return <div className="text-center p-4">Loading...</div>; // Display loading while data is being fetched

  return (
    <div className="max-w-full mx-auto p-2 lg:p-4 bg-white shadow-2xl rounded-lg lg:mt-3">
      <h2 className="text-xl sm:text-2xl font-semibold text-center mb-2 sm:mb-4 lg:mb-6">
        <i className="lg:hidden sm:hidden mr-2 fas fa-calendar-alt text-red-500"></i> Datesheets for {student.class} class
      </h2>

      {examDatesheets.map((examDatesheet, index) => {
        const subjects = Array.isArray(examDatesheet.subjects) ? examDatesheet.subjects : [];
        const sortedSubjects = sortByExamDate(subjects);

        return (
          <div key={index} className="mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-4">
              <i className="lg:hidden sm:hidden mr-2 fas fa-file-alt text-green-600 sm:text-green-800"></i> Exam: {examDatesheet.examName}
            </h3>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-center">#</th>
                    <th className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-center">Subject</th>
                    <th className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-center">Date</th>
                    <th className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-center">Timing</th>
                    <th className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-center">Room</th>
                    <th className="py-2 px-2 sm:px-4 text-xs sm:text-sm text-center">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-gray-100 divide-y divide-gray-200">
                  {sortedSubjects.map((subject, idx) => (
                    <tr key={idx} className="text-center text-xs sm:text-sm">
                      <td className="border px-2 py-2">{idx + 1}</td>
                      <td className="border px-2 py-2">{subject.subjectName}</td>
                      <td className="border px-2 py-2">{formatDate(subject.examDate)}</td>
                      <td className="border px-2 py-2">{subject.startTime} - {subject.endTime}</td>
                      <td className="border px-2 py-2">{subject.room}</td>
                      <td className="border px-2 py-2">{subject.notes || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Button to download PDF for each datesheet */}
            <button 
              className="bg-blue-800 hover:bg-blue-600 text-white py-2 px-2  rounded mt-4" 
              onClick={() => generatePDF(examDatesheet)}
            >
              <i className='lg:hidden sm:hidden fas fa-download mr-2'></i>Download PDF for {examDatesheet.examName}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default ExamDatesheet;
